// src/fleet-stats/analytics/buildActivityGrid.js
//
// Robot × period grid — "how far did each robot drive, week by week".
//
// Every other view answers this one robot at a time (filter to a robot, read
// the daily chart) or one period at a time (set the range, read the per-robot
// table). Comparing several robots across several weeks meant re-filtering once
// per robot or re-exporting once per week. This is the grid that falls out of
// the data directly: distance is already per robot per day, so a week is a sum.

const MS_DAY = 86400000;

/** ISO-8601 week: weeks start Monday, week 1 contains the first Thursday. */
export function isoWeekKey(iso) {
    const d = new Date(iso + "T00:00:00Z");
    const day = d.getUTCDay() || 7;                  // Sunday is 7, not 0
    d.setUTCDate(d.getUTCDate() + 4 - day);          // move to this week's Thursday
    const year = d.getUTCFullYear();
    const jan1 = new Date(Date.UTC(year, 0, 1));
    const week = Math.ceil(((d - jan1) / MS_DAY + 1) / 7);
    return { key: `${year}-W${String(week).padStart(2, "0")}`, label: `W${String(week).padStart(2, "0")}` };
}

export function periodKey(iso, grain) {
    if (grain === "day") return { key: iso, label: iso.slice(5) };      // MM-DD
    if (grain === "month") return { key: iso.slice(0, 7), label: iso.slice(0, 7) };
    return isoWeekKey(iso);
}

/**
 * @param rows    flattened rows, already filtered to the view the user is on
 * @param grain   "day" | "week" | "month"
 * @param metric  "km" | "hours"
 *                "hours" is moving_time_s — time actually DRIVING, matching
 *                what "hours" means everywhere else in the dashboard. Uptime
 *                (powered on) is a different field and is not offered here.
 * @param limit   keep the busiest N robots; the rest are summarised
 */
export function buildActivityGrid(rows, grain = "week", metric = "km", limit = 40) {
    if (!rows || rows.length === 0) return null;

    const periods = new Map();                       // key -> label
    const byRobot = new Map();

    for (const r of rows) {
        if (!r.date) continue;
        const { key, label } = periodKey(r.date, grain);
        periods.set(key, label);

        let e = byRobot.get(r.robotId);
        if (!e) {
            e = {
                robotId: r.robotId,
                robotName: r.robotName || r.robotId,
                siteName: r.siteName,
                client: r.client,
                cells: new Map(),
                total: 0,
                activePeriods: new Set(),
            };
            byRobot.set(r.robotId, e);
        }
        const value = metric === "hours"
            ? (r.moving_time_s || 0) / 3600
            : (r.distance_m || 0) / 1000;

        e.cells.set(key, (e.cells.get(key) || 0) + value);
        e.total += value;
        if (value > 0) e.activePeriods.add(key);
    }

    const periodKeys = Array.from(periods.keys()).sort();
    const columns = periodKeys.map((key) => ({ key, label: periods.get(key) }));

    let robots = Array.from(byRobot.values()).sort((a, b) => b.total - a.total);
    const shown = robots.slice(0, limit);
    const hidden = robots.slice(limit);

    // A period average over ALL columns would read as "this robot is slacking"
    // for a unit that only arrived last month. Averaging over the periods it was
    // actually present keeps the number honest.
    const build = (e) => ({
        robotId: e.robotId,
        robotName: e.robotName,
        siteName: e.siteName,
        client: e.client,
        cells: periodKeys.map((k) => e.cells.get(k) || 0),
        total: e.total,
        average: e.activePeriods.size ? e.total / e.activePeriods.size : 0,
    });

    const totals = periodKeys.map((k) =>
        robots.reduce((s, e) => s + (e.cells.get(k) || 0), 0));

    return {
        grain,
        metric,
        unit: metric === "hours" ? "driving h" : "km",
        columns,
        rows: shown.map(build),
        hiddenCount: hidden.length,
        hiddenTotal: hidden.reduce((s, e) => s + e.total, 0),
        totals,
        grandTotal: totals.reduce((s, v) => s + v, 0),
        robotCount: robots.length,
    };
}
